import React, {useMemo} from "react";
import {RemoteValue} from "../../../util/RemoteValue";
import {useEmployeeStore} from "../../../api/EmployeeStore";
import {useEmployeeKeyHistoryStore} from "../../../api/EmployeeKeyHistoryStore";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Link as RouterLink} from "react-router-dom";
import {Stack} from "@mui/material";
import {KeyChip} from "../../../components/key-chip/KeyChip";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import {EmployeeHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {Employees_Read, KeyData_Read} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {Employee, KeySnapshot} from "../../../api/Model";

type EmployeeWithMultipleKeysProblem = {employee: Employee, keys: KeySnapshot[]};

const employeesWithMultipleKeysCsvModel = RemoteValue.fromValue(new CsvModel<EmployeeWithMultipleKeysProblem>([
    {header: 'Person', value: ({employee}) => employee.fullName},
    {header: 'Email', value: ({employee}) => employee.email},
    {header: 'Keys', value: ({keys}) => keys.map(({key}) => key.keyNumber).join(', ')},
]));

export function EmployeesWithMultipleKeysWidget() {
    const pageUrl = usePageUrl();
    const employees = useEmployeeStore(state => state.remoteValue);
    const keyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const problems = useMemo(() => RemoteValue.combine({employees, keyHistory})
        .map(values => values.employees.flatMap(employee => {
            const keys = values.keyHistory.active(employee);
            return !employee.deactivated && keys.length > 1 ? [{employee, keys}] : [];
        })), [employees, keyHistory]);

    return (
        <DashboardWidget
            title="Employees with multiple keys"
            description="Usually, one key is enough for an employee to access an office. This is the list of employees who currently hold more than one key."
            problems={problems}
            requiredPermissions={[Employees_Read, KeyData_Read]}
            link={pageUrl(AppPages.employees, {}, {hasKey: 'many-keys', active: 'active'})}
            csvModel={employeesWithMultipleKeysCsvModel}
            renderProblem={({employee, keys}, index) => (
                <DashboardWidgetItem
                    key={employee.id}
                    index={index}
                    left={
                        <>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee}/>
                        </>
                    }
                    right={
                        <>
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                {keys.map(({key, historyRecord}) => (
                                    <RouterLink key={key.id} to={pageUrl(AppPages.keys, {}, {search: key.keyNumber})}>
                                        <KeyChip keyData={key} record={historyRecord}/>
                                    </RouterLink>
                                ))}
                            </Stack>
                            <EmployeeHistoryButton employee={employee}/>
                        </>
                    }
                />
            )}
        />
    );
}
